import React from "react";

import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/src/lib/utils";

import { formatCount } from "../charts/charts.utils";

import type { LucideIcon } from "lucide-react";
import type { JSX } from "react";

import type { CustomerTransactionsKpiMetric } from "@/src/api/StatsAPI.types";

type Trend = "up" | "down" | "flat";

interface KpiProgressionProps {
  metric: CustomerTransactionsKpiMetric;
  isInverse?: boolean;
}

interface TrendDefinition {
  Icon: LucideIcon;
  textKey: string;
  prefix: string;
}

const TREND = {
  up: { Icon: TrendingUp, textKey: "percentUp", prefix: "+" },
  down: { Icon: TrendingDown, textKey: "percentDown", prefix: "-" },
  flat: { Icon: Minus, textKey: "percentEqual", prefix: "" },
} as const satisfies Record<Trend, TrendDefinition>;

function getTrend(progression: number): Trend {
  if (progression > 0) return "up";
  return progression < 0 ? "down" : "flat";
}

function getTrendClassName(trend: Trend, isGood: boolean): string {
  if (trend === "flat") return "text-muted-foreground";
  if (isGood) return "text-status-active";
  return "text-status-error";
}

function KpiProgression({
  metric,
  isInverse,
}: KpiProgressionProps): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  const trend = getTrend(metric.progression);
  const { Icon: TrendIcon, textKey, prefix } = TREND[trend];
  const isGood = trend === (isInverse ? "down" : "up");
  const trendClassName = getTrendClassName(trend, isGood);

  return (
    <span className="flex items-center gap-1.5">
      <TrendIcon
        aria-hidden
        className={cn("size-4 shrink-0", trendClassName)}
      />
      <span className={trendClassName}>
        {`${Math.round(metric.percentage)}% ${t(textKey).trim()} (${prefix}${formatCount(Math.abs(metric.progression), lang)})`}
      </span>
    </span>
  );
}

export default KpiProgression;
